import React, { useMemo, useState } from 'react'
import fetch from 'isomorphic-unfetch'
import CONFIG from 'isomorphic-config'
import { Autocomplete } from '@pcln/horizon'
import useTypeAhead from '@/components/TypeAhead/useTypeAhead'
import type { LOCATION_SEARCH_TYPE, TypeAheadConfigEndpoint } from '@/types'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useBootstrapData from '@/hooks/useBootstrapData'
import fireTypeaheadClickEvent, {
  fireAutocompleteOpenEvent
} from '@/components/TypeAhead/ga4'
import { mapTypeToCode, recentSearchesKey } from './constants'

export default function AutocompleteWrapper({
  searchKey,
  ref,
  typeAheadEndpoint = 'hotels',
  onItemSelect,
  initialSelectedItem,
  errorMessage,
  label
}: {
  searchKey: string
  onItemSelect?: (item: LOCATION_SEARCH_TYPE) => void
  ref?: React.Ref<HTMLInputElement>
  initialSelectedItem?: LOCATION_SEARCH_TYPE
  typeAheadEndpoint?: TypeAheadConfigEndpoint
  errorMessage?: string
  label?: string
}) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn }
  } = useBootstrapData()

  // recent search
  const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
  const [recentStartLocations, recentEndLocations] =
    useRecentSearchesLocalStorage(currentRecentSearchesKey)
  const recentSearchesLocalStorage = searchKey.includes('startLocation')
    ? recentStartLocations
    : recentEndLocations
  const recentSearchesFetched = useRecentSearchesFetched(
    clientGUID,
    appName,
    appVersion,
    signedIn
  )
  const recentSearches =
    typeAheadEndpoint === 'hotels'
      ? recentSearchesFetched.slice(0, 3)
      : recentSearchesLocalStorage.slice(0, 3)

  // recommended search
  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn
  })
  const recommendedSearches = useRecommendations(recommendedCityIds)
  const recentOrRecommended =
    recommendedSearches.length > 0 ? recommendedSearches : recentSearches
  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(
    null
  )

  const typeAheadHorizon = useTypeAhead({
    searchValue: autocompleteValue,
    searchKey,
    onLoaded: () => null,
    debounceTime: 100,
    typeAheadEndpoint
  })

  const { autocompleteData, listType } = useMemo(() => {
    if (autocompleteValue === null || autocompleteValue === '') {
      if (recentOrRecommended?.length > 0) {
        return {
          autocompleteData: [
            {
              isGroupHeader: true,
              itemName:
                recommendedSearches.length > 0
                  ? 'Recommended Searches'
                  : 'Recent Searches'
            },
            ...recentOrRecommended
          ],
          listType:
            recommendedSearches.length > 0 ? 'recommended' : 'recent_searches'
        }
      }
    }
    if (typeAheadHorizon.items.length > 0) {
      return { autocompleteData: typeAheadHorizon.items, listType: 'typein' }
    }
    return { autocompleteData: [], listType: undefined }
  }, [
    recentOrRecommended,
    typeAheadHorizon,
    autocompleteValue,
    recommendedSearches.length
  ])

  const handleTypeAheadFeedback = ({
    entered,
    id,
    itemName,
    displayName,
    type,
    selectedPosition,
    skipFeedbackCall
  }: LOCATION_SEARCH_TYPE & {
    selectedPosition: number | null
    skipFeedbackCall?: boolean
  }) => {
    if (skipFeedbackCall) return

    // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
    const url = `${
      CONFIG.client.typeahead.feedback[typeAheadEndpoint]
    }/${id}/${encodeURIComponent(entered ?? 'null')}/${encodeURIComponent(
      itemName || displayName || ''
    )}/${selectedPosition ?? ''}`
    const queryParams = `language=en&locale=en-US&placeType=${mapTypeToCode[type]}`
    void fetch(`${url}?${queryParams}`)
  }

  return (
    <Autocomplete
      inputRef={ref}
      errorMessage={errorMessage}
      label={label}
      autocompleteData={autocompleteData}
      handleInputChange={(inputValue: string) => {
        setAutocompleteValue(inputValue)
      }}
      handleSelectItem={(selectedItem, highlightedIndex) => {
        onItemSelect?.(selectedItem as LOCATION_SEARCH_TYPE)
        fireTypeaheadClickEvent(typeAheadEndpoint, listType)
        if ('isGroupHeader' in selectedItem) return

        handleTypeAheadFeedback({
          ...selectedItem,
          selectedPosition: highlightedIndex || null
        })
      }}
      initialSelectedItem={initialSelectedItem}
      handleOnMenuOpen={() => {
        const updatedListType =
          listType === 'typein' && !autocompleteValue ? undefined : listType
        fireAutocompleteOpenEvent(typeAheadEndpoint, updatedListType)
      }}
    />
  )
}
